import React, { useEffect, useMemo, useState } from 'react';
import {
  BriefcaseBusiness,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  Search,
  ShieldAlert,
  ShieldCheck,
  UserMinus,
  Users,
} from 'lucide-react';
import { useParams } from 'react-router-dom';

import { BaseLayout } from '@/shared/components/layout';
import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';
import { useUser } from '@/shared/hooks/useUser';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { cn } from '@/shared/utils/utils';

import {
  useAdministratorsMutation,
  useClientProjectMembershipMutation,
  useDeactivateOrganizationMemberMutation,
  useClientOrganizationMembers,
  useClientProjectMembers,
  useProjectPermissions,
  useProjectRoles,
} from '../hooks';
import type { ClientProjectMembership, OrganizationUser } from '../types';
import { RolePermissionsPanel } from './RolePermissionsPanel';

const userLabel = (user: { first_name?: string; last_name?: string; email: string }) =>
  [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email;

const formatDate = (value?: string | null) => {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(value));
};

const statusBadgeClass = (active: boolean) =>
  active
    ? 'border-success/20 bg-success/15 text-success'
    : 'border-destructive/20 bg-destructive/10 text-destructive';

const roleBadgeClass = (isAdmin: boolean) =>
  isAdmin
    ? 'border-primary/25 bg-primary/12 text-primary'
    : 'border-border bg-muted text-muted-foreground';

const PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const ALL_PROJECTS_VALUE = 'all-projects';
const SELECT_PROJECT_VALUE = 'select-project';

type ProjectMemberStatusFilter = 'all' | 'active' | 'inactive';
type ProjectRoleFilter = 'all' | 'admin' | 'non-admin';
type ProjectStatusFilter = 'all' | 'active' | 'inactive';

interface ProjectMemberFilters {
  memberStatus: ProjectMemberStatusFilter;
  role: ProjectRoleFilter;
  projectStatus: ProjectStatusFilter;
}

type OrganizationStatusFilter = 'all' | 'active' | 'inactive';

interface OrganizationFilters {
  status: OrganizationStatusFilter;
}

const defaultProjectFilters: ProjectMemberFilters = {
  memberStatus: 'all',
  role: 'all',
  projectStatus: 'all',
};

const defaultOrganizationFilters: OrganizationFilters = {
  status: 'all',
};

interface StatChipProps {
  label: string;
  value: string | number;
}

const StatChip: React.FC<StatChipProps> = ({ label, value }) => (
  <div className="rounded-[13px] border border-border bg-card px-4 py-3 shadow-slate">
    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
      {label}
    </p>
    <p className="mt-1 font-display text-2xl font-extrabold tracking-[-0.02em] text-foreground">
      {value}
    </p>
  </div>
);

interface SearchBoxProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  className?: string;
}

const SearchBox: React.FC<SearchBoxProps> = ({ value, placeholder, onChange, className }) => (
  <div
    className={cn(
      'flex w-full items-center gap-2.5 rounded-xl border border-border bg-card px-3.5 py-2.5 text-muted-foreground shadow-slate',
      className,
    )}
  >
    <Search className="size-4 shrink-0" strokeWidth={1.9} />
    <Input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-auto border-0 bg-transparent p-0 text-sm text-foreground shadow-none outline-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
    />
  </div>
);

const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-[1.375rem] border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
    {children}
  </div>
);

interface ProjectOption {
  id: number;
  name: string;
  alias?: string | null;
  isActive: boolean;
  memberCount: number;
}

interface ProjectPickerDialogProps {
  projects: ProjectOption[];
  selectedProjectId: string;
  onSelect: (value: string) => void;
  triggerLabel?: string;
  triggerClassName?: string;
}

const ProjectPickerDialog: React.FC<ProjectPickerDialogProps> = ({
  projects,
  selectedProjectId,
  onSelect,
  triggerLabel,
  triggerClassName,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const activeProjects = useMemo(
    () => projects.filter((project) => project.isActive),
    [projects],
  );
  const completedProjects = useMemo(
    () => projects.filter((project) => !project.isActive),
    [projects],
  );

  const filterProjects = (items: ProjectOption[]) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((project) =>
      [project.name, project.alias].filter(Boolean).join(' ').toLowerCase().includes(needle),
    );
  };

  const selectAndClose = (value: string) => {
    onSelect(value);
    setOpen(false);
    setSearch('');
  };

  const ProjectButton = ({
    project,
    subdued = false,
  }: {
    project: ProjectOption;
    subdued?: boolean;
  }) => {
    const selected = selectedProjectId === String(project.id);

    return (
      <button
        key={project.id}
        type="button"
        className={cn(
          'flex w-full items-center justify-between gap-3 rounded-[13px] border px-4 py-3 text-left transition-colors',
          selected
            ? 'border-primary/30 bg-primary/12 ring-1 ring-primary/30'
            : 'border-border bg-card hover:bg-accent',
          subdued && 'opacity-80',
        )}
        onClick={() => selectAndClose(String(project.id))}
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-foreground">
            {project.name}
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {project.alias || (project.isActive ? 'Active project' : 'Completed project')}
          </span>
        </span>
        <span className="shrink-0 rounded-full border border-border bg-muted px-2.5 py-1 text-[11px] font-bold text-muted-foreground">
          {project.memberCount}
        </span>
      </button>
    );
  };

  const filteredActive = filterProjects(activeProjects);
  const filteredCompleted = filterProjects(completedProjects);
  const selectedProject = projects.find((project) => String(project.id) === selectedProjectId);
  const label =
    triggerLabel ??
    (selectedProjectId === ALL_PROJECTS_VALUE
      ? 'All Projects'
      : selectedProject?.name ?? 'Select project');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch('');
      }}
    >
      <Button
        type="button"
        variant={selectedProjectId === SELECT_PROJECT_VALUE ? 'default' : 'outline'}
        className={cn('rounded-[11px]', triggerClassName)}
        onClick={() => setOpen(true)}
      >
        <BriefcaseBusiness className="mr-2 size-4" strokeWidth={1.9} />
        {label}
      </Button>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Select project</DialogTitle>
          <DialogDescription>
            Active projects are prioritized. Older projects remain available below when needed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="Search projects..."
            className="shadow-none"
          />

          <div className="max-h-[52vh] space-y-5 overflow-y-auto pr-1">
            <section>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Active projects
                </p>
                <Badge variant="secondary" className="rounded-full border border-border bg-muted">
                  {activeProjects.length}
                </Badge>
              </div>
              {filteredActive.length === 0 ? (
                <p className="rounded-[13px] border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No active projects match your search.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {filteredActive.map((project) => (
                    <ProjectButton key={project.id} project={project} />
                  ))}
                </div>
              )}
            </section>

            <section>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center justify-between rounded-[13px] border px-4 py-3 text-left transition-colors',
                  selectedProjectId === ALL_PROJECTS_VALUE
                    ? 'border-primary/30 bg-primary/12 ring-1 ring-primary/30'
                    : 'border-border bg-muted hover:bg-accent',
                )}
                onClick={() => selectAndClose(ALL_PROJECTS_VALUE)}
              >
                <span>
                  <span className="block text-sm font-semibold text-foreground">All Projects</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    Show every project membership for this client.
                  </span>
                </span>
                <span className="rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-bold text-muted-foreground">
                  {projects.reduce((sum, project) => sum + project.memberCount, 0)}
                </span>
              </button>
            </section>

            <section>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Completed projects
                </p>
                <Badge variant="secondary" className="rounded-full border border-border bg-muted">
                  {completedProjects.length}
                </Badge>
              </div>
              {filteredCompleted.length === 0 ? (
                <p className="rounded-[13px] border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No completed projects match your search.
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {filteredCompleted.map((project) => (
                    <ProjectButton key={project.id} project={project} subdued />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface FilterSelectProps<T extends string> {
  label: string;
  value: T;
  options: Array<{ label: string; value: T }>;
  onChange: (value: T) => void;
}

const FilterSelect = <T extends string>({
  label,
  value,
  options,
  onChange,
}: FilterSelectProps<T>) => (
  <div className="space-y-2">
    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
      {label}
    </p>
    <Select value={value} onValueChange={(next) => onChange(next as T)}>
      <SelectTrigger className="h-10 rounded-[11px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

interface ProjectFiltersDialogProps {
  filters: ProjectMemberFilters;
  onChange: (filters: ProjectMemberFilters) => void;
  activeCount: number;
}

const ProjectFiltersDialog: React.FC<ProjectFiltersDialogProps> = ({
  filters,
  onChange,
  activeCount,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const reset = () => {
    setDraft(defaultProjectFilters);
    onChange(defaultProjectFilters);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) setDraft(filters);
      }}
    >
      <Button type="button" variant="outline" className="rounded-[11px]" onClick={() => setOpen(true)}>
        <Filter className="mr-2 size-4" strokeWidth={1.9} />
        Filters
        {activeCount > 0 && (
          <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
            {activeCount}
          </span>
        )}
      </Button>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Project member filters</DialogTitle>
          <DialogDescription>
            Narrow project membership rows by role, membership status, and project status.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <FilterSelect
            label="Role"
            value={draft.role}
            onChange={(role) => setDraft((current) => ({ ...current, role }))}
            options={[
              { label: 'All roles', value: 'all' },
              { label: 'Project admins', value: 'admin' },
              { label: 'Non-admin roles', value: 'non-admin' },
            ]}
          />
          <FilterSelect
            label="Membership status"
            value={draft.memberStatus}
            onChange={(memberStatus) => setDraft((current) => ({ ...current, memberStatus }))}
            options={[
              { label: 'Any status', value: 'all' },
              { label: 'Active only', value: 'active' },
              { label: 'Inactive only', value: 'inactive' },
            ]}
          />
          <FilterSelect
            label="Project status"
            value={draft.projectStatus}
            onChange={(projectStatus) => setDraft((current) => ({ ...current, projectStatus }))}
            options={[
              { label: 'Any project', value: 'all' },
              { label: 'Active projects', value: 'active' },
              { label: 'Inactive projects', value: 'inactive' },
            ]}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={reset}>
            Reset
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={apply}>Apply filters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface OrganizationFiltersDialogProps {
  filters: OrganizationFilters;
  onChange: (filters: OrganizationFilters) => void;
  activeCount: number;
}

const OrganizationFiltersDialog: React.FC<OrganizationFiltersDialogProps> = ({
  filters,
  onChange,
  activeCount,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(filters);

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const reset = () => {
    setDraft(defaultOrganizationFilters);
    onChange(defaultOrganizationFilters);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) setDraft(filters);
      }}
    >
      <Button type="button" variant="outline" className="rounded-[11px]" onClick={() => setOpen(true)}>
        <Filter className="mr-2 size-4" strokeWidth={1.9} />
        Filters
        {activeCount > 0 && (
          <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
            {activeCount}
          </span>
        )}
      </Button>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Organization member filters</DialogTitle>
          <DialogDescription>
            Narrow organization members by account status.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <FilterSelect
            label="Account status"
            value={draft.status}
            onChange={(status) => setDraft((current) => ({ ...current, status }))}
            options={[
              { label: 'Any status', value: 'all' },
              { label: 'Active only', value: 'active' },
              { label: 'Inactive only', value: 'inactive' },
            ]}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={reset}>
            Reset
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={apply}>Apply filters</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TablePagerProps {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const TablePager: React.FC<TablePagerProps> = ({
  page,
  pageSize,
  pageCount,
  total,
  onPageChange,
  onPageSizeChange,
}) => {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-1 text-sm text-muted-foreground">
      <span>
        Showing {start}-{end} of {total}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium">Rows</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-9 w-[82px] rounded-[10px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-[10px]"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="mr-1 size-3.5" />
          Previous
        </Button>
        <span className="min-w-20 text-center text-xs font-semibold text-foreground">
          {page} / {pageCount}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-[10px]"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
        >
          Next
          <ChevronRight className="ml-1 size-3.5" />
        </Button>
      </div>
    </div>
  );
};

interface ProjectAdminActionProps {
  membership: ClientProjectMembership;
  projectAdminIds: number[];
}

const ProjectAdminAction: React.FC<ProjectAdminActionProps> = ({ membership, projectAdminIds }) => {
  const { toast } = useToast();
  const mutation = useAdministratorsMutation(membership.project);
  const isAdmin = membership.role.is_admin;

  const toggleProjectAdmin = async () => {
    try {
      const nextAdminIds = new Set(projectAdminIds);
      if (isAdmin) {
        nextAdminIds.delete(membership.user.id);
      } else {
        nextAdminIds.add(membership.user.id);
      }

      await mutation.mutateAsync({
        user_ids: Array.from(nextAdminIds),
      });
      toast({
        title: isAdmin
          ? `${userLabel(membership.user)} is no longer a project admin.`
          : `${userLabel(membership.user)} is now a project admin.`,
      });
    } catch (error) {
      toast({
        title: 'Unable to update project admin',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <Button
      type="button"
      variant={isAdmin ? 'ghost' : 'outline'}
      size="sm"
      className="rounded-[10px]"
      onClick={() => void toggleProjectAdmin()}
      disabled={mutation.isPending || !membership.is_active}
    >
      {mutation.isPending ? (
        <>
          <Loader2 className="mr-2 size-3.5 animate-spin" />
          Saving
        </>
      ) : isAdmin ? (
        'Remove admin'
      ) : (
        'Make admin'
      )}
    </Button>
  );
};

interface SelectedRoleForPermissions {
  projectId: number;
  roleId: number;
  roleName: string;
  projectName: string;
}

interface RolePermissionsDialogProps {
  selectedRole: SelectedRoleForPermissions | null;
  onOpenChange: (open: boolean) => void;
}

const RolePermissionsDialog: React.FC<RolePermissionsDialogProps> = ({
  selectedRole,
  onOpenChange,
}) => {
  const { data: roles = [], isLoading: rolesLoading } = useProjectRoles(selectedRole?.projectId);
  const { data: permissions = [], isLoading: permissionsLoading } = useProjectPermissions(
    selectedRole?.projectId,
  );

  const role = useMemo(
    () => roles.find((candidate) => candidate.id === selectedRole?.roleId) ?? null,
    [roles, selectedRole],
  );
  const includedPermissionIds = useMemo(
    () => role?.permissions.map((permission) => permission.id) ?? [],
    [role],
  );
  const loading = rolesLoading || permissionsLoading;

  return (
    <Dialog open={selectedRole !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{role?.name ?? selectedRole?.roleName ?? 'Role permissions'}</DialogTitle>
          <DialogDescription>
            {selectedRole
              ? `${selectedRole.projectName} role permissions and access scope.`
              : 'Role permissions and access scope.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <Skeleton className="h-72 rounded-[13px]" />
        ) : role ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {role.is_admin && <Badge variant="destructive">Admin</Badge>}
              {role.is_default && <Badge variant="secondary">Default</Badge>}
              {!role.is_admin && !role.is_default && <Badge variant="outline">Custom</Badge>}
              <Badge variant="outline">
                {includedPermissionIds.length} of {permissions.length} permissions
              </Badge>
            </div>
            <RolePermissionsPanel
              mode="view"
              permissions={permissions}
              includedPermissionIds={includedPermissionIds}
            />
          </div>
        ) : (
          <div className="rounded-[13px] border border-dashed border-border p-4 text-sm text-muted-foreground">
            Unable to load permissions for this role.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface RemoveProjectMemberDialogProps {
  membership: ClientProjectMembership | null;
  onOpenChange: (open: boolean) => void;
}

const RemoveProjectMemberDialog: React.FC<RemoveProjectMemberDialogProps> = ({
  membership,
  onOpenChange,
}) => {
  const { toast } = useToast();
  const { removeMembership } = useClientProjectMembershipMutation();

  const removeMember = async () => {
    if (!membership) return;
    try {
      await removeMembership.mutateAsync({
        projectId: membership.project,
        membershipId: membership.id,
      });
      toast({
        title: `${userLabel(membership.user)} removed from ${membership.project_name}.`,
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Unable to remove member',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={membership !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Remove project member</DialogTitle>
          <DialogDescription>
            {membership
              ? `Remove ${userLabel(membership.user)} from ${membership.project_name}? They will lose access to this project.`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={removeMembership.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void removeMember()}
            disabled={removeMembership.isPending}
          >
            {removeMembership.isPending ? (
              <>
                <Loader2 className="mr-2 size-3.5 animate-spin" />
                Removing
              </>
            ) : (
              'Remove member'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ChangeProjectRoleDialogProps {
  membership: ClientProjectMembership | null;
  onOpenChange: (open: boolean) => void;
}

const ChangeProjectRoleDialog: React.FC<ChangeProjectRoleDialogProps> = ({
  membership,
  onOpenChange,
}) => {
  const { toast } = useToast();
  const { changeRole } = useClientProjectMembershipMutation();
  const { data: roles = [], isLoading: rolesLoading } = useProjectRoles(membership?.project);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  useEffect(() => {
    setSelectedRoleId(membership ? String(membership.role.id) : '');
  }, [membership]);

  const selectedRole = roles.find((role) => String(role.id) === selectedRoleId);

  const submitRoleChange = async () => {
    if (!membership || !selectedRoleId || selectedRoleId === String(membership.role.id)) return;
    try {
      await changeRole.mutateAsync({
        projectId: membership.project,
        membershipId: membership.id,
        payload: { role_id: Number(selectedRoleId) },
      });
      toast({
        title: `${userLabel(membership.user)} is now ${selectedRole?.name ?? 'assigned to the selected role'}.`,
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Unable to change role',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={membership !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change role</DialogTitle>
          <DialogDescription>
            {membership
              ? `Update ${userLabel(membership.user)}'s role on ${membership.project_name}.`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Role
          </p>
          {rolesLoading ? (
            <Skeleton className="h-10 rounded-[11px]" />
          ) : (
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger className="h-10 rounded-[11px]">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={changeRole.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void submitRoleChange()}
            disabled={
              changeRole.isPending ||
              rolesLoading ||
              !selectedRoleId ||
              selectedRoleId === String(membership?.role.id)
            }
          >
            {changeRole.isPending ? (
              <>
                <Loader2 className="mr-2 size-3.5 animate-spin" />
                Saving
              </>
            ) : (
              'Change role'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ProjectMembersTableProps {
  memberships: ClientProjectMembership[];
  isLoading: boolean;
  isError: boolean;
}

const ProjectMembersTable: React.FC<ProjectMembersTableProps> = ({
  memberships,
  isLoading,
  isError,
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [selectedProjectId, setSelectedProjectId] = useState(SELECT_PROJECT_VALUE);
  const [filters, setFilters] = useState<ProjectMemberFilters>(defaultProjectFilters);
  const [selectedRole, setSelectedRole] = useState<SelectedRoleForPermissions | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<ClientProjectMembership | null>(null);
  const [pendingRoleChange, setPendingRoleChange] = useState<ClientProjectMembership | null>(null);

  const projects = useMemo(() => {
    const byId = new Map<number, ProjectOption>();

    memberships.forEach((membership) => {
      const existing = byId.get(membership.project);
      if (existing) {
        existing.memberCount += 1;
        return;
      }
      byId.set(membership.project, {
        id: membership.project,
        name: membership.project_name,
        alias: membership.project_alias,
        isActive: membership.project_is_active,
        memberCount: 1,
      });
    });

    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [memberships]);

  const filtered = useMemo(() => {
    if (selectedProjectId === SELECT_PROJECT_VALUE) return [];

    const needle = search.trim().toLowerCase();
    return memberships.filter((membership) => {
      if (
        selectedProjectId !== ALL_PROJECTS_VALUE &&
        membership.project !== Number(selectedProjectId)
      ) {
        return false;
      }
      if (filters.memberStatus === 'active' && !membership.is_active) return false;
      if (filters.memberStatus === 'inactive' && membership.is_active) return false;
      if (filters.role === 'admin' && !membership.role.is_admin) return false;
      if (filters.role === 'non-admin' && membership.role.is_admin) return false;
      if (filters.projectStatus === 'active' && !membership.project_is_active) return false;
      if (filters.projectStatus === 'inactive' && membership.project_is_active) return false;
      if (!needle) return true;

      const haystack = [
        membership.project_name,
        membership.project_alias,
        userLabel(membership.user),
        membership.user.email,
        membership.role.name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [filters, memberships, search, selectedProjectId]);

  const adminIdsByProject = useMemo(() => {
    const byProject = new Map<number, number[]>();
    memberships.forEach((membership) => {
      if (!membership.is_active || !membership.role.is_admin) return;
      const ids = byProject.get(membership.project) ?? [];
      ids.push(membership.user.id);
      byProject.set(membership.project, ids);
    });
    return byProject;
  }, [memberships]);

  const activeFilterCount = [
    filters.memberStatus !== defaultProjectFilters.memberStatus,
    filters.role !== defaultProjectFilters.role,
    filters.projectStatus !== defaultProjectFilters.projectStatus,
  ].filter(Boolean).length;
  const hasProjectSelection = selectedProjectId !== SELECT_PROJECT_VALUE;
  const selectedProjectName =
    selectedProjectId === ALL_PROJECTS_VALUE
      ? 'All Projects'
      : projects.find((project) => project.id === Number(selectedProjectId))?.name;
  const selectProject = (value: string) => {
    setSelectedProjectId(value);
    setSearch('');
    setPage(1);
  };
  const projectSelect = (
    <ProjectPickerDialog
      projects={projects}
      selectedProjectId={selectedProjectId}
      onSelect={selectProject}
      triggerLabel={selectedProjectName ?? 'Select project'}
      triggerClassName="w-full justify-start lg:w-[320px]"
    />
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  if (isLoading) return <Skeleton className="h-80 rounded-[1.375rem]" />;
  if (isError) {
    return (
      <div className="rounded-[1.375rem] border border-destructive/30 bg-card p-6 shadow-slate">
        <p className="text-sm text-destructive">Unable to load project members.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Project roster
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Select a project to review membership and role assignments.
          </p>
        </div>
        {hasProjectSelection && (
          <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:items-center">
            {projectSelect}
            <ProjectFiltersDialog
              filters={filters}
              onChange={(nextFilters) => {
                setFilters(nextFilters);
                setPage(1);
              }}
              activeCount={activeFilterCount}
            />
            <SearchBox
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              placeholder="Search members, roles..."
              className="lg:w-[340px]"
            />
          </div>
        )}
      </div>

      {!hasProjectSelection ? (
        <EmptyState>
          <div className="mx-auto max-w-md space-y-4">
            <div className="space-y-2">
              <p className="font-display text-lg font-bold tracking-[-0.02em] text-foreground">
                Select a project to view members
              </p>
              <p>
              Choose an active project to focus the roster, or choose All Projects for a
              client-wide view.
              </p>
            </div>
            <ProjectPickerDialog
              projects={projects}
              selectedProjectId={selectedProjectId}
              onSelect={selectProject}
              triggerLabel="Select project"
              triggerClassName="mx-auto h-11 min-w-[220px] rounded-[11px]"
            />
          </div>
        </EmptyState>
      ) : filtered.length === 0 ? (
        <EmptyState>
          No project members match {selectedProjectName ? selectedProjectName : 'this selection'}.
        </EmptyState>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-[13px] border border-border bg-card px-4 py-3 shadow-slate">
            <div>
              <p className="text-sm font-semibold text-foreground">{selectedProjectName}</p>
              <p className="text-xs text-muted-foreground">
                {filtered.length} matching {filtered.length === 1 ? 'member' : 'members'}
              </p>
            </div>
            {activeFilterCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters(defaultProjectFilters);
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
          <div className="overflow-hidden rounded-[1.375rem] border border-border bg-card shadow-slate">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Project</TableHead>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((membership) => (
                  <TableRow key={membership.id}>
                    <TableCell>
                      <div className="min-w-[180px]">
                        <p className="font-semibold text-foreground">{membership.project_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {membership.project_alias ||
                            (membership.project_is_active ? 'Active project' : 'Inactive project')}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="min-w-[210px]">
                        <p className="font-medium text-foreground">{userLabel(membership.user)}</p>
                        <p className="text-xs text-muted-foreground">{membership.user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="rounded-full text-left transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        onClick={() =>
                          setSelectedRole({
                            projectId: membership.project,
                            roleId: membership.role.id,
                            roleName: membership.role.name,
                            projectName: membership.project_name,
                          })
                        }
                        aria-label={`View permissions for ${membership.role.name} on ${membership.project_name}`}
                      >
                        <Badge
                          className={cn(
                            'rounded-full border px-2.5 py-1 hover:bg-accent',
                            roleBadgeClass(membership.role.is_admin),
                          )}
                        >
                          {membership.role.is_admin && <ShieldCheck className="mr-1 size-3" />}
                          {membership.role.name}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="rounded-full text-left transition-transform hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        onClick={() => setPendingRemoval(membership)}
                        disabled={!membership.is_active}
                        aria-label={`Remove ${userLabel(membership.user)} from ${membership.project_name}`}
                      >
                        <Badge
                          className={cn(
                            'rounded-full border px-2.5 py-1 hover:bg-accent',
                            statusBadgeClass(membership.is_active),
                            !membership.is_active && 'cursor-not-allowed opacity-60',
                          )}
                        >
                          {membership.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(membership.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-[10px]"
                          onClick={() => setPendingRoleChange(membership)}
                          disabled={!membership.is_active}
                        >
                          Change Role
                        </Button>
                        <ProjectAdminAction
                          membership={membership}
                          projectAdminIds={adminIdsByProject.get(membership.project) ?? []}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <TablePager
            page={currentPage}
            pageSize={pageSize}
            pageCount={pageCount}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            }}
          />
        </>
      )}
      <RolePermissionsDialog
        selectedRole={selectedRole}
        onOpenChange={(open) => {
          if (!open) setSelectedRole(null);
        }}
      />
      <RemoveProjectMemberDialog
        membership={pendingRemoval}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null);
        }}
      />
      <ChangeProjectRoleDialog
        membership={pendingRoleChange}
        onOpenChange={(open) => {
          if (!open) setPendingRoleChange(null);
        }}
      />
    </div>
  );
};

interface OrganizationMembersTableProps {
  members: OrganizationUser[];
  isLoading: boolean;
  isError: boolean;
  currentUserId?: number | string;
}

const OrganizationMembersTable: React.FC<OrganizationMembersTableProps> = ({
  members,
  isLoading,
  isError,
  currentUserId,
}) => {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [filters, setFilters] = useState<OrganizationFilters>(defaultOrganizationFilters);
  const [pendingDeactivate, setPendingDeactivate] = useState<OrganizationUser | null>(null);
  const deactivateMember = useDeactivateOrganizationMemberMutation();

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return members.filter((member) => {
      if (filters.status === 'active' && !member.is_active) return false;
      if (filters.status === 'inactive' && member.is_active) return false;
      if (!needle) return true;

      const haystack = [
        userLabel(member),
        member.email,
        member.phone_number,
        member.job_title,
        member.is_staff || member.is_superuser ? 'staff' : '',
        member.is_client_admin ? 'client admin' : 'member',
        member.is_active ? 'active' : 'inactive',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [filters, members, search]);

  const activeFilterCount = [
    filters.status !== defaultOrganizationFilters.status,
  ].filter(Boolean).length;

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const confirmDeactivate = async () => {
    if (!pendingDeactivate) return;
    try {
      await deactivateMember.mutateAsync(pendingDeactivate.id);
      toast({ title: `${userLabel(pendingDeactivate)} has been deactivated.` });
      setPendingDeactivate(null);
    } catch (error) {
      toast({
        title: 'Unable to deactivate member',
        description: handleApiError(error),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) return <Skeleton className="h-80 rounded-[1.375rem]" />;
  if (isError) {
    return (
      <div className="rounded-[1.375rem] border border-destructive/30 bg-card p-6 shadow-slate">
        <p className="text-sm text-destructive">Unable to load organization members.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Organization directory
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Global account details for every client user.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:items-center">
          <OrganizationFiltersDialog
            filters={filters}
            onChange={(nextFilters) => {
              setFilters(nextFilters);
              setPage(1);
            }}
            activeCount={activeFilterCount}
          />
          <SearchBox
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Search members..."
            className="lg:w-[340px]"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState>No organization members match this view.</EmptyState>
      ) : (
        <>
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-[13px] border border-border bg-card px-4 py-3 shadow-slate">
              <p className="text-sm text-muted-foreground">
                {filtered.length} matching {filtered.length === 1 ? 'member' : 'members'}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters(defaultOrganizationFilters);
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            </div>
          )}
          <div className="overflow-hidden rounded-[1.375rem] border border-border bg-card shadow-slate">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone Number</TableHead>
                  <TableHead>Job Title</TableHead>
                  <TableHead>Project Memberships</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((member) => {
                  const isSelf = String(member.id) === String(currentUserId);
                  const isStaffish = member.is_staff || member.is_superuser;
                  return (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="min-w-[170px]">
                          <p className="font-semibold text-foreground">{userLabel(member)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{member.email}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {member.phone_number || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {member.job_title || '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className="rounded-full border border-border bg-muted px-2.5 py-1 text-muted-foreground"
                        >
                          {member.project_membership_count}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            'rounded-full border px-2.5 py-1',
                            statusBadgeClass(member.is_active),
                          )}
                        >
                          {member.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(member.last_login)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="rounded-[10px] text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setPendingDeactivate(member)}
                          disabled={!member.is_active || isSelf || isStaffish}
                        >
                          <UserMinus className="mr-2 size-3.5" />
                          Deactivate
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePager
            page={currentPage}
            pageSize={pageSize}
            pageCount={pageCount}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            }}
          />
        </>
      )}

      <Dialog
        open={pendingDeactivate !== null}
        onOpenChange={(open) => !open && setPendingDeactivate(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Deactivate account</DialogTitle>
            <DialogDescription>
              {pendingDeactivate
                ? `Deactivate ${userLabel(pendingDeactivate)}? They will no longer be able to sign in.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDeactivate(null)}
              disabled={deactivateMember.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmDeactivate()}
              disabled={deactivateMember.isPending}
            >
              {deactivateMember.isPending ? (
                <>
                  <Loader2 className="mr-2 size-3.5 animate-spin" />
                  Deactivating
                </>
              ) : (
                'Deactivate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ClientAdminPage: React.FC = () => {
  const { clientName } = useParams<{ clientName: string }>();
  const { user, loading } = useUser();
  const {
    data: projectMembers = [],
    isLoading: projectMembersLoading,
    isError: projectMembersError,
  } = useClientProjectMembers();
  const {
    data: organizationMembers = [],
    isLoading: organizationMembersLoading,
    isError: organizationMembersError,
  } = useClientOrganizationMembers();

  const canManage = Boolean(user?.is_staff || user?.is_superuser || user?.is_client_admin);
  const stats = useMemo(() => {
    const activeUsers = organizationMembers.filter((member) => member.is_active).length;
    const projectAdmins = projectMembers.filter(
      (membership) => membership.is_active && membership.role.is_admin,
    ).length;
    const activeProjects = new Set(
      projectMembers
        .filter((membership) => membership.project_is_active)
        .map((membership) => membership.project),
    ).size;

    return {
      activeUsers,
      projectAdmins,
      activeProjects,
    };
  }, [organizationMembers, projectMembers]);

  if (loading) {
    return (
      <BaseLayout>
        <div className="mx-auto w-full max-w-[1400px] space-y-5 px-4 py-8 sm:px-6 lg:px-9">
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-9 w-72" />
          </div>
          <Skeleton className="h-80 rounded-[1.375rem]" />
        </div>
      </BaseLayout>
    );
  }

  if (!canManage) {
    return (
      <BaseLayout>
        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-9">
          <div className="rounded-[1.375rem] border border-destructive/30 bg-card p-6 shadow-slate">
            <div className="flex items-center gap-2.5">
              <ShieldAlert className="size-5 shrink-0 text-destructive" strokeWidth={1.9} />
              <h1 className="font-display text-lg font-bold tracking-[-0.02em]">
                Access restricted
              </h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Client administration is available to client admins and staff only.
            </p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="mx-auto w-full max-w-[1400px] px-4 py-8 sm:px-6 lg:px-9">
        <div className="mb-[22px] flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[13.5px] font-semibold text-muted-foreground">
              {clientName ?? 'Client'}
            </div>
            <div className="mt-1 flex items-center gap-3">
              <ShieldCheck className="size-6 shrink-0 text-primary" strokeWidth={1.9} />
              <h1 className="font-display text-3xl font-extrabold tracking-[-0.02em] md:text-[2.4rem]">
                Client Administration
              </h1>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Manage organization accounts, project membership visibility, and project
              administrator access from one client-wide workspace.
            </p>
          </div>
        </div>

        <div className="mb-5 grid gap-3 sm:grid-cols-3">
          <StatChip label="Active users" value={stats.activeUsers} />
          <StatChip label="Active projects" value={stats.activeProjects} />
          <StatChip label="Project admins" value={stats.projectAdmins} />
        </div>

        <Tabs defaultValue="project-members" className="space-y-5">
          <TabsList>
            <TabsTrigger value="project-members" className="gap-2.5">
              <BriefcaseBusiness className="size-4" strokeWidth={1.9} />
              Project Members
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-[11.5px] font-bold text-muted-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                {projectMembers.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="organization-members" className="gap-2.5">
              <Users className="size-4" strokeWidth={1.9} />
              Organization Members
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-[11.5px] font-bold text-muted-foreground">
                {organizationMembers.length}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="project-members" className="mt-0">
            <ProjectMembersTable
              memberships={projectMembers}
              isLoading={projectMembersLoading}
              isError={projectMembersError}
            />
          </TabsContent>

          <TabsContent value="organization-members" className="mt-0">
            <OrganizationMembersTable
              members={organizationMembers}
              isLoading={organizationMembersLoading}
              isError={organizationMembersError}
              currentUserId={user?.id}
            />
          </TabsContent>
        </Tabs>

        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="size-3.5 text-success" strokeWidth={1.9} />
          Account deactivation blocks sign-in without deleting project history.
        </div>
      </div>
    </BaseLayout>
  );
};
